import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { FormatService } from '../../../../common/format/format.service';
import { audienceFeesAt, estimateRankMultiplier, quotePieces, ratesFromPricingModel } from '@/app/common/brief-pieces/piece-price';

interface TierCard {
  title: string;
  range: string;
  perCreator: number;
  perCreatorLabel: string;
  bonusValue: number;
}

/**
 * Read-only compensation breakdown shown on the wizard's Review step.
 * Regular-user cash pricing: base price + videos come from the selected pricing model
 * (already set earlier in the wizard); per-tier and totals are derived, never edited here.
 */
@Component({
  selector: 'bb-compensation-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DecimalPipe],
  templateUrl: './compensation-section.html',
  styleUrl: './compensation-section.scss'
})
export class CompensationSection {
  private readonly service = inject(CreateBriefService);
  private readonly format = inject(FormatService);

  readonly currencySymbol = computed(() => this.service.pricingModelCurrencySymbol());
  readonly videos = computed(() => {
    const pieces = this.service.pieces();
    return pieces?.length ? pieces.length : this.service.videosPerCreator();
  });
  readonly creators = computed(() => this.service.requiredCreators());
  readonly basePrice = computed(() => {
    const pieces = this.service.pieces();
    if (!pieces?.length) {
      return this.service.getBasePrice();
    }
    const net = quotePieces({
      pieces,
      rates: ratesFromPricingModel(this.service.selectedPricingModel()),
      rankMultiplier: 1,
      audienceFees: {},
      adLicenseSelected: this.service.selectedAdvertisingOnly(),
    }).net;
    return net / pieces.length;
  });
  /** UGC-only briefs are a flat per-video price — no follower-tier bonuses. */
  readonly isUgcOnly = computed(() => {
    const pieces = this.service.pieces();
    if (!pieces?.length) {
      return this.service.selectedUgcOnly();
    }
    return pieces.every((piece) => piece.placements.every((placement) => placement.kind !== 'social'));
  });

  /** One card per influencer level on the selected pricing model (posted briefs only). */
  readonly tierCards = computed<TierCard[]>(() => {
    if (this.isUgcOnly()) {
      return [];
    }
    const levels = this.service.selectedPricingModel()?.influencerLevels ?? [];
    return levels.map((level) => {
      const perCreator = this.getExpectedPayment(level.bonusValue);
      return {
        title: level.title,
        range: `${this.format.convertIntToString(level.followersFrom)} – ${this.format.convertIntToString(level.followersTo)}`,
        perCreator,
        perCreatorLabel: this.money(perCreator),
        bonusValue: level.bonusValue,
      };
    });
  });

  /** Per-creator payout across all tiers. With no tiers, the piece quote at a zero audience fee. */
  private readonly perCreatorValues = computed<number[]>(() => {
    const cards = this.tierCards();
    if (cards.length) {
      return cards.map((c) => c.perCreator);
    }
    return [this.getExpectedPayment(0)];
  });

  readonly pricePerCreatorLabel = computed(() =>
    this.moneyRange(Math.min(...this.perCreatorValues()), Math.max(...this.perCreatorValues())),
  );

  readonly estimatedTotalLabel = computed(() => {
    const creators = this.creators();
    const values = this.perCreatorValues();
    return this.moneyRange(Math.min(...values) * creators, Math.max(...values) * creators);
  });

  /** Format a value as prefixed currency (e.g. £1,234.00), matching the pricing model's currency. */
  private money(value: number): string {
    return this.currencySymbol() + value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /** A single value when both ends match, otherwise a "min – max" range. */
  private moneyRange(min: number, max: number): string {
    return min === max ? this.money(min) : `${this.money(min)} – ${this.money(max)}`;
  }

  getExpectedPayment(bonusValue: number) {
    const pieces = this.service.pieces();
    if (!pieces?.length) {
      return this.service.getExpectedPayment(bonusValue);
    }
    const model = this.service.selectedPricingModel();
    return quotePieces({
      pieces,
      rates: ratesFromPricingModel(model),
      rankMultiplier: estimateRankMultiplier(model?.ranks),
      audienceFees: audienceFeesAt(pieces, bonusValue / 100),
      adLicenseSelected: this.service.selectedAdvertisingOnly(),
    }).net;
  }
}
